import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Routes, useLocation } from "react-router-dom";

/** Wraps `<Routes>` in an AnimatePresence so every route change cross-fades
 *  smoothly with a subtle slide-and-blur. The animation respects the user's
 *  reduced-motion preference (skipped when set).
 *
 *  Implementation notes:
 *  - We key the wrapper on the FIRST PATH SEGMENT only (e.g. `/dashboard`
 *    for both `/dashboard/client` and `/dashboard/hotel`) so deep sub-route
 *    changes inside a section don't trigger a full screen transition.
 *  - Logout (`/dashboard/...` → `/welcome` or `/login`) DOES animate because
 *    the segment changes — exactly what the user asked for.
 *  - `mode="wait"` ensures the outgoing screen finishes its exit animation
 *    before the next one starts, preventing jarring overlap.
 */
export default function RouteTransition({ children }) {
  const location = useLocation();
  const reduce = typeof window !== "undefined"
    && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const segment = (location.pathname.split("/")[1] || "root").toLowerCase();

  // Reduced-motion OR Capacitor natif : pas de motion.div wrapper.
  //
  // POURQUOI : sur Chromium WebView Android, le `filter: blur(0px)` +
  // transform `y: 0` que framer-motion laisse en style inline crée un
  // "compositor layer isolé" qui casse les hit-tests des enfants en
  // `position: absolute` avec z-index. Résultat : sur le
  // ClientDashboard (qui empile ClientHero absolute + top-bar absolute
  // z-40 + sheet absolute z-30), les taps sur les boutons n'atteignaient
  // JAMAIS les handlers React. Le fix `overflow-hidden` était juste une
  // couche du problème — le vrai coupable était CE wrapper motion.
  //
  // Sur natif on renonce donc à l'anim de transition (le rendu route est
  // instantané) mais on gagne une interaction 100 % fiable sur Android
  // ET iOS. Sur web classique on garde l'anim, ça marche parfaitement.
  const isNative = typeof window !== "undefined"
    && window.Capacitor
    && typeof window.Capacitor.isNativePlatform === "function"
    && window.Capacitor.isNativePlatform();

  if (reduce || isNative) {
    return <Routes location={location}>{children}</Routes>;
  }
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={segment}
        initial={{ opacity: 0, y: 14, filter: "blur(8px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)", transitionEnd: { filter: "none", transform: "none" } }}
        exit={{ opacity: 0, y: -10, filter: "blur(6px)" }}
        transition={{
          duration: 0.42,
          ease: [0.22, 1, 0.36, 1], // softCubic / out-expo-ish
        }}
        data-testid="route-transition-wrapper"
      >
        <Routes location={location}>{children}</Routes>
      </motion.div>
    </AnimatePresence>
  );
}
